/**
 * YourCampaignsCard — the races this account is attached to, and the turf each
 * one granted.
 *
 * Why this exists
 * ---------------
 * A volunteer can work two down-ballot races: one login, two
 * `user_candidate_links`. Until now nothing in the app said so. Permissions
 * live on the ACCOUNT (one role) and turf arrives as one flat union of
 * `user_region_scopes`, so a staffer on Jim's and Lorry's campaigns saw a
 * single undifferentiated app and had no way to answer "who am I doing this
 * for?" outside the slate chip at a door.
 *
 * **This is disclosure, not a switcher, and the distinction is the whole
 * design.** A campaign switcher would be a lie: the server unions the scopes
 * and filters every read against that union, so "you are in Lorry's campaign"
 * would be a client-side label over data that still includes Jim's voters.
 * Making the switch real means per-link scopes and an active-campaign filter
 * on every query — a much larger change, and until it lands the honest UI is
 * one that tells you what you hold rather than pretending to fence it.
 *
 * Renders on BOTH home screens because the people it is for are split across
 * them: HomeView for everyone, and CanvassView's HomeScreen because a
 * CANVASSER never sees HomeView at all (`homeViewForRole` sends them to their
 * doors) — and CANVASSER is the role delegation mints most often.
 *
 * Renders NOTHING while loading, on error, or with no links. An admin has no
 * candidate links and must not get an empty box; the canvasser home's start
 * CTA must not shift under a skeleton. Same contract as RevisitQueuePanel.
 */
import React, { useEffect, useState } from 'react';
import { Flag } from 'lucide-react';

import { listMyCandidates, type MyCandidate } from '@/api/delegation';

/**
 * What turf to show for one link.
 *
 * `my_scopes` — the rows this candidate actually granted THIS account — is the
 * honest answer wherever it exists. Falling back to the candidate's whole
 * reach for a staffer would show a volunteer holding two precincts the entire
 * district, which reads as access they do not have.
 *
 * The two empty cases are different facts, so they get different sentences: a
 * candidate's own link carries no grant rows (their turf IS the reach), while
 * an empty staff grant could be an admin's doing, a pre-attribution row, or a
 * grant this candidate has since narrowed away — so it names none of them.
 */
function turfLine(c: MyCandidate): string {
  // `?? []` on both, despite the types: this renders inside the canvasser's
  // door screen, where a missing key would throw during render and blank the
  // walk UI. Neither list is worth that.
  const mine = c.my_scopes ?? [];
  if (mine.length > 0) {
    return mine.map((s) => s.label).join(' · ');
  }
  if (c.relation === 'candidate') {
    const reach = c.scopes ?? [];
    return reach.length > 0 ? reach.map((s) => s.label).join(' · ') : 'County-wide';
  }
  // Not "set by an admin": this row is also what a WITHDRAWN grant looks
  // like, once a candidate's reach edit narrowed everything they gave away.
  // Naming a source we cannot verify would be a guess printed as a fact.
  return 'No turf recorded for this campaign';
}

export const YourCampaignsCard: React.FC = () => {
  const [rows, setRows] = useState<MyCandidate[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    listMyCandidates(controller.signal)
      .then((r) => {
        // Array-guarded, not just typed. This card renders INSIDE the
        // canvasser's door screen, so a payload that isn't a list — a stubbed
        // client, a proxy returning an error object with a 200 — would throw
        // during render and take the whole walk UI down with it. A non-list is
        // the same outcome as no links: nothing.
        if (!controller.signal.aborted) setRows(Array.isArray(r) ? r : []);
      })
      // Silent: this card is context, never the reason someone opened the
      // screen. A red banner here would out-shout the work.
      .catch(() => {});
    return () => controller.abort();
  }, []);

  if (!rows || rows.length === 0) return null;

  return (
    <section
      aria-labelledby="your-campaigns-heading"
      className="bg-bg-raised rounded-lg border border-border-base p-4"
    >
      <h3
        id="your-campaigns-heading"
        className="text-sm font-semibold text-fg-secondary uppercase tracking-wide mb-2 flex items-center gap-2"
      >
        <Flag className="w-4 h-4 text-accent" aria-hidden="true" />
        {rows.length === 1 ? 'Your campaign' : 'Your campaigns'}
      </h3>
      <ul className="divide-y divide-border-base">
        {rows.map((c) => (
          <li
            key={c.candidate_id}
            className="py-2 flex items-start justify-between gap-3"
          >
            <div className="min-w-0">
              <div className="text-fg-primary text-sm font-medium truncate">
                {c.full_name}
              </div>
              <div className="text-xs text-fg-muted">
                {c.office}
                {c.district_label ? ` · ${c.district_label}` : ''}
              </div>
              <div className="text-xs text-fg-secondary mt-0.5">
                {turfLine(c)}
              </div>
            </div>
            <span className="flex-shrink-0 inline-flex items-center rounded-full bg-bg-elevated text-fg-secondary border border-border-base px-2 py-0.5 text-xs font-semibold">
              {c.relation === 'candidate' ? 'Your race' : 'Volunteer'}
            </span>
          </li>
        ))}
      </ul>
      {rows.length > 1 && (
        // Said out loud because the alternative is someone inferring a fence
        // that is not there. Their role and their turf are the union of both
        // races at once; nothing in the app is scoped to "the campaign I am
        // currently in", because there is no such state.
        <p className="text-xs text-fg-muted mt-2">
          You work both races with one account — your turf is everything listed
          here at once. Each walk shows which candidates it is for.
        </p>
      )}
    </section>
  );
};
